/**
 * Parse a flexible time string into decimal hours. Accepts:
 *   "1.5" · "1,5" · "0.02"            → plain (decimal) hours
 *   "1h 30min" · "1h30m" · "2h" · "45m" · "90min"  → hours/minutes
 * Returns null when nothing parseable / non-positive.
 */
export function parseHours(raw: string): number | null {
  const s = raw.trim().toLowerCase().replace(',', '.');
  if (!s) return null;
  // Plain decimal hours.
  if (/^\d*\.?\d+$/.test(s)) {
    const n = Number(s);
    return n > 0 ? n : null;
  }
  // h / min components (either or both may be present).
  let total = 0;
  let matched = false;
  const h = s.match(/(\d*\.?\d+)\s*h/);
  if (h) {
    total += Number(h[1]);
    matched = true;
  }
  const m = s.match(/(\d*\.?\d+)\s*m(?:in)?/);
  if (m) {
    total += Number(m[1]) / 60;
    matched = true;
  }
  if (!matched) return null;
  return total > 0 ? total : null;
}

/**
 * Parse a flexible time string into **whole minutes** — the unit worklogs are
 * stored in. Same input forms as `parseHours` ("1h 20min", "1.5", "90m").
 * Returns null when nothing parseable / non-positive.
 *
 * Prefer this over `parseHours` for worklogs: decimal hours can't represent
 * 20/40-minute increments exactly, so round-tripping through them loses time.
 */
export function parseMinutes(raw: string): number | null {
  const hours = parseHours(raw);
  if (hours == null) return null;
  const minutes = Math.round(hours * 60);
  return minutes > 0 ? minutes : null;
}

/**
 * Render whole minutes as a human string:
 *   90 → "1h 30min" · 120 → "2h" · 30 → "30min" · 0 → "0h"
 */
export function formatMinutes(value: number | string | null | undefined): string {
  const total = Math.round(Number(value));
  if (!Number.isFinite(total) || total <= 0) return '0h';
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h && m) return `${h}h ${m}min`;
  if (h) return `${h}h`;
  return `${m}min`;
}

/**
 * Render decimal hours as a human string — the inverse of `parseHours`:
 *   1.5 → "1h 30min" · 2 → "2h" · 0.5 → "30min" · 0.02 → "1min" · 0 → "0h"
 * Storage + math stay decimal; this is display-only.
 */
export function formatHours(value: string | number): string {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '0h';
  let h = Math.floor(n);
  let m = Math.round((n - h) * 60);
  if (m === 60) {
    h += 1;
    m = 0;
  }
  if (h && m) return `${h}h ${m}min`;
  if (h) return `${h}h`;
  return `${m}min`;
}

/**
 * Render a work-item estimate for display. Points → the plain number ("3",
 * "0.5"); hours-mode → "1h 30m" from the stored **whole minutes** (90 → "1h 30m",
 * 120 → "2h", 45 → "45m"). Empty/zero → "".
 */
export function formatEstimate(
  value: string | number | null | undefined,
  isHours: boolean,
): string {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (!isHours) return String(n); // story points, as-entered
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/**
 * Parse an estimate input to its stored value. Points → the number. Hours-mode →
 * **whole minutes**, accepting "1h 30m" / "90m" / "2h" / "1h30min" or a bare
 * decimal read as hours ("1.5" → 90). Returns null for empty/unparseable.
 */
export function parseEstimate(raw: string, isHours: boolean): number | null {
  const s = raw.trim();
  if (!s) return null;
  if (!isHours) {
    const n = Number(s.replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  const hours = parseHours(s); // decimal hours (or null)
  return hours == null ? null : Math.round(hours * 60);
}
